import { useLocation } from "react-router-dom";

import { Heading, Navbar, NavbarItem, NavbarLabel, NavbarSection } from "../../f7";
import { awaitsUnwiredProduct, isTabPath, type Section, type Tab } from "../../nav/sections";
import { productById } from "../../products/registry";

/**
 * The section heading and its tab strip, as one block.
 *
 * THE HEADING IS THE SECTION, not the tab. This is the design's structure and
 * the reason every page in it looks like the same console: "Tenants" with
 * `All tenants · Blocks & suspensions · Fleet evidence` beneath it, rather
 * than each tab arriving with its own H1 and its own idea of the gap under it.
 * A tab already names itself in the strip; repeating that name as a page title
 * says it twice and moves everything below it down by a heading.
 *
 * A single-tab section renders nothing here — one tab is a heading with extra
 * steps, and the page supplies its own (Overview does exactly this).
 */
export function SectionHeader({ section }: { section: Section }) {
  const { pathname } = useLocation();

  if (section.tabs.length < 2) return null;
  // A DRILL-IN owns its own heading — the merchant record is headed by the
  // merchant, not by "Tenants", and its Breadcrumbs already carry the way back
  // to the section. This is the design's own rule: it excludes the tenant
  // record from the tab strip for exactly this reason.
  if (!isTabPath(pathname)) return null;

  return (
    <div
      className="flex flex-col gap-1 border-b border-slate-950/10 dark:border-white/10"
      style={{ paddingBottom: "10px" }}
    >
      <Heading>{section.label}</Heading>
      <Tabs section={section} />
    </div>
  );
}

/**
 * The strip itself.
 *
 * These are LINKS, not ARIA tabs, and the distinction is deliberate. A real
 * tablist swaps panels within one page and announces itself as such; these
 * change the URL, so a screen reader should say "link" and the browser's back
 * button should work. Borrowing `role="tablist"` would describe the widget
 * accurately and the behaviour wrongly.
 */
export function Tabs({ section }: { section: Section }) {
  const { pathname } = useLocation();

  if (section.tabs.length < 2) return null;

  return (
    <Navbar>
      <NavbarSection className="flex-wrap">
        {section.tabs.map((tab) => (
          <NavbarItem key={tab.to} href={tab.to} current={isCurrent(section, tab, pathname)}>
            <NavbarLabel>
              {tab.label}
              {/* WHICH product, not a bare "soon". Every unbacked tab in this
                  console is waiting on the same thing — Labeeb's admin API —
                  and naming it turns four mystery tabs into one known
                  dependency. "soon" invites "how soon?", which nobody here can
                  answer. */}
              {tab.comingSoon && (
                <span className="ml-1.5 text-[10px] tracking-wide text-slate-400 uppercase dark:text-slate-500">
                  {awaitsUnwiredProduct(tab)
                    ? tab.products.map((id) => productById(id)?.label ?? id).join(" · ")
                    : "soon"}
                </span>
              )}
            </NavbarLabel>
          </NavbarItem>
        ))}
      </NavbarSection>
    </Navbar>
  );
}

/**
 * A tab owns a child route only when no SIBLING claims it.
 *
 * /tenants/access is a child of /tenants by string, but it is its own tab — so
 * a plain prefix test lights both "All tenants" and "Blocks & suspensions" at
 * once, and a highlight that marks two things marks nothing. Sibling tabs win
 * their own paths; the parent keeps the genuine drill-ins (/tenants/507077,
 * /policy/caps/plus).
 */
function isCurrent(section: Section, tab: Tab, pathname: string): boolean {
  const claimedBySibling = section.tabs.some(
    (other) =>
      other.to !== tab.to &&
      other.to.startsWith(`${tab.to}/`) &&
      (pathname === other.to || pathname.startsWith(`${other.to}/`)),
  );
  return pathname === tab.to || (!claimedBySibling && pathname.startsWith(`${tab.to}/`));
}
